// First-run prompt: "Who is this app for?" Lays the groundwork for the
// V1.2 sub-profile model (CLAUDE-RESEARCH-GAP in pages/Family.tsx).
// In V1 the answer only swaps copy ("your medicines" -> "{name}'s
// medicines") and is persisted to accessibility_preferences.profileKind.
//
// Focus group #15 (Gloria, Jennifer, Mei, Priya — all acute in round 2):
// "the app still assumes the user IS the patient."

import { type FormEvent, useEffect, useState } from 'react';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import { useMe } from '../hooks/useMe';

type Kind = 'self' | 'caregiver';

// Round-3 fix #12: defer the prompt until the user has visited at
// least 2 distinct screens. Earl, James, Walter all flagged the prompt
// appearing on first home view as overreach before showing value. This
// cookie tracks visit count; the prompt waits until count >= 2.
const VISIT_COUNT_COOKIE = 'medreview_visit_count';

function readVisitCount(): number {
  if (typeof document === 'undefined') return 0;
  const m = document.cookie.match(new RegExp(`(?:^|; )${VISIT_COUNT_COOKIE}=([0-9]+)`));
  return m ? Number(m[1]) || 0 : 0;
}

function bumpVisitCount(): number {
  if (typeof document === 'undefined') return 0;
  const next = readVisitCount() + 1;
  document.cookie = `${VISIT_COUNT_COOKIE}=${next}; path=/; max-age=${60 * 60 * 24 * 30}; samesite=lax`;
  return next;
}

export function CaregiverKindFirstRun(): JSX.Element | null {
  const authedFetch = useAuthedFetch();
  const { me } = useMe();
  const [chosen, setChosen] = useState<Kind | null>(null);
  const [subjectName, setSubjectName] = useState('');
  const [dismissed, setDismissed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [visits, setVisits] = useState(0);

  // Bump the visit-count cookie on mount and read the new value.
  // Renders nothing until count >= 2.
  useEffect(() => {
    setVisits(bumpVisitCount());
  }, []);

  if (!me) return null;
  if (dismissed) return null;
  if (me.accessibility?.profileKindChosen === true) return null;
  if (visits < 2) return null;

  const savePick = async (kind: Kind, name?: string) => {
    setSaving(true);
    try {
      await authedFetch('/api/me/accessibility', {
        method: 'PATCH',
        body: JSON.stringify({
          profileKind: kind,
          profileSubjectName: name?.trim() || undefined,
          profileKindChosen: true,
        }),
      });
      // Caregiver branch: create the managed subject's profile so the
      // active-subject switcher in the header surfaces it immediately.
      // The self profile was auto-created by the V1.1 migration.
      if (kind === 'caregiver' && name?.trim()) {
        const { notifyProfilesChanged } = await import('./ProfileSwitcher');
        await authedFetch('/api/profiles', {
          method: 'POST',
          body: JSON.stringify({ subjectName: name.trim() }),
        }).catch(() => null);
        // Wake the ProfileSwitcher so it refetches and renders the new
        // managed subject without waiting for the next mount. Round-3
        // regression fix.
        notifyProfilesChanged();
      }
    } catch {
      // best-effort; we still dismiss locally
    } finally {
      setSaving(false);
      setDismissed(true);
    }
  };

  const onCaregiverSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (!subjectName.trim()) return;
    void savePick('caregiver', subjectName);
  };

  return (
    <section
      aria-labelledby="caregiver-prompt-title"
      style={{
        background: 'var(--color-surface-secondary)',
        border: '1.5px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-md)',
        margin: '0 0 var(--space-md)',
      }}
    >
      <h3 id="caregiver-prompt-title" style={{ margin: '0 0 8px', fontSize: 20 }}>
        Who is this app for?
      </h3>
      <p style={{ margin: '0 0 12px', fontSize: 16, color: 'var(--color-text-secondary)' }}>
        We'll match the wording. You can change this later in Settings.
      </p>

      {chosen === 'caregiver' ? (
        <form
          onSubmit={onCaregiverSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>Loved one's first name</span>
            <input
              type="text"
              value={subjectName}
              onChange={(e) => setSubjectName(e.target.value)}
              placeholder="Marv"
              style={{
                padding: '14px 16px',
                fontSize: 18,
                minHeight: 48,
                border: '1.5px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
              }}
            />
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="submit"
              disabled={saving || !subjectName.trim()}
              className="home-hero__cta"
              style={{ minHeight: 48 }}
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setChosen(null)}
              className="ghost-cta"
              style={{ minHeight: 48 }}
            >
              Back
            </button>
          </div>
        </form>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button
            type="button"
            onClick={() => void savePick('self')}
            disabled={saving}
            className="home-hero__cta"
            style={{ minHeight: 48, minWidth: 160 }}
          >
            Me
          </button>
          <button
            type="button"
            onClick={() => setChosen('caregiver')}
            disabled={saving}
            className="ghost-cta"
            style={{ minHeight: 48, minWidth: 160 }}
          >
            A loved one
          </button>
          <button
            type="button"
            onClick={() => void savePick('self')}
            disabled={saving}
            style={{
              background: 'transparent',
              border: 0,
              color: 'var(--color-text-secondary)',
              fontSize: 14,
              cursor: 'pointer',
              alignSelf: 'center',
              padding: '8px 12px',
            }}
          >
            Just exploring
          </button>
        </div>
      )}
    </section>
  );
}
